import React from 'react';
import { deleteStatsAlarm, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, IUserStore, } from '@Store';
import { CrudEnum, IPreviewStatsAlarm, PermissionEnum, TStatsAlarmSortableListCol, } from 'atlas-shared';
import { getFullName, tableColumnDatetimeSorter, tableColumnStringSorter, timeSince, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listCloneButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchLikeProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const StatsAlarmListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, users: IUserStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewStatsAlarm) => React.ReactElement> = [], filters: IWithListProps<IPreviewStatsAlarm, TStatsAlarmSortableListCol>['filters'], sorter: IWithListProps<IPreviewStatsAlarm, TStatsAlarmSortableListCol>['sorter']): ColumnsType<IPreviewStatsAlarm> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewStatsAlarm, b: IPreviewStatsAlarm) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewStatsAlarm['organization_id'], stats_alarm: IPreviewStatsAlarm) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewStatsAlarm, b: IPreviewStatsAlarm) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('USER'),
      dataIndex: 'user_id',
      key: 'user_id',
      render: (user_id: IPreviewStatsAlarm['user_id'], stats_alarm: IPreviewStatsAlarm) => user_id === -1 ? t('SYS_USER') : users.dict?.[user_id] ? getFullName(users.dict[user_id]) : t('USER_NOT_FOUND', { replace: { user_id, }, }),
      sorter: (a: IPreviewStatsAlarm, b: IPreviewStatsAlarm) => (users.dict?.[a.user_id] ? a.user_id === -1 ? t('SYS_USER') : getFullName(users.dict?.[a.user_id]) : '').localeCompare(users.dict?.[b.user_id] ? getFullName(users.dict?.[b.user_id]) : ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'user_id')?.order || null,
      filteredValue: filters.user_id || null,
      ...GetColumnSearchProps(t, 'user_id', t('USER'), users.users),
      className: 'table-cell-user_id',
    },
    {
      title: t('TITLE'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IPreviewStatsAlarm, b: IPreviewStatsAlarm) => tableColumnStringSorter(a.title, b.title),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'title')?.order || null,
      filteredValue: filters.title || null,
      ...GetColumnSearchLikeProps(t, 'title', t('TITLE')),
      className: 'table-cell-title',
    },
    {
      title: t('LAST_MATCH_AT'),
      dataIndex: 'last_match_at',
      key: 'last_match_at',
      render: (last_match_at: IPreviewStatsAlarm['last_match_at'], stats_alarm: IPreviewStatsAlarm) => <div className='date-column'>{last_match_at ? timeSince(t, new Date(last_match_at), true) : ''}</div>,
      sorter: (a: IPreviewStatsAlarm, b: IPreviewStatsAlarm) => tableColumnDatetimeSorter(a.last_match_at, b.last_match_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'last_match_at')?.order || null,
      className: 'table-cell-last_match_at',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewStatsAlarm['created_at'], stats_alarm: IPreviewStatsAlarm) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewStatsAlarm, b: IPreviewStatsAlarm) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewStatsAlarm) => (
        <div className={'table-operation-wrp'}>
          {buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Insert, PermissionEnum.StatsAlarm, row) && listCloneButton(t, navigate, 'stats_alarm', row)}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'stats_alarm', row)}
          {abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.StatsAlarm, row) && listEditButton(t, navigate, 'stats_alarm', row)}
          {abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.StatsAlarm, row) && listDeleteButton(t, row, deleteStatsAlarm)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewStatsAlarm>;

};
